import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export interface DetectionRow {
  id: string;
  camera_id: string;
  detection_type: string;
  confidence: number;
  bounding_box?: Record<string, unknown> | null;
  snapshot_ref?: string | null;
  inference_location: "edge" | "server";
  status: "ingested" | "emitted" | "failed";
  occurred_at?: unknown;
  created_at?: unknown;
  extra: Record<string, unknown>;
}

export function useDetections(filters?: {
  camera_id?: string;
  detection_type?: string;
  since?: string; // ISO datetime — maps to backend ?since=
  until?: string; // ISO datetime — maps to backend ?until=
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ["sinsight", "detections", filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.camera_id) params.set("camera_id", filters.camera_id);
      if (filters?.detection_type)
        params.set("detection_type", filters.detection_type);
      if (filters?.since) params.set("since", filters.since);
      if (filters?.until) params.set("until", filters.until);
      if (filters?.limit !== undefined)
        params.set("limit", String(filters.limit));
      if (filters?.offset !== undefined)
        params.set("offset", String(filters.offset));
      const { data } = await api.get<{
        items: DetectionRow[]; total: number;
      }>(`/api/v1/sinsight/detections?${params.toString()}`);
      return data;
    },
    refetchInterval: 30_000,
  });
}

// ---------------------------------------------------------------------------
// S16 — analytics schedules (closed-hours windows)
// ---------------------------------------------------------------------------
export interface ScheduleWindow {
  days?: number[] | null;
  start: string; // "HH:MM"
  end: string; // "HH:MM"
}

export interface AnalyticsSchedule {
  id: string;
  // S16.5 — config is subject-keyed under the hood; camera_id/entity_id
  // are derived from the subject's sview360 binding for display.
  subject_id?: string;
  camera_id?: string | null;
  entity_id?: string | null;
  name: string;
  timezone: string;
  windows: ScheduleWindow[];
  is_active: boolean;
  created_at?: unknown;
  updated_at?: unknown;
}

const SCHEDULES_KEY = ["sinsight", "schedules"] as const;
const MODULE_CONFIG_KEY = ["sinsight", "module-config"] as const;

export function useSchedules(filters?: {
  camera_id?: string;
  entity_id?: string;
}) {
  return useQuery({
    queryKey: [...SCHEDULES_KEY, filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.camera_id) params.set("camera_id", filters.camera_id);
      if (filters?.entity_id) params.set("entity_id", filters.entity_id);
      const { data } = await api.get<{
        items: AnalyticsSchedule[]; total: number;
      }>(`/api/v1/sinsight/schedules?${params.toString()}`);
      return data;
    },
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      timezone: string;
      windows: ScheduleWindow[];
      camera_id?: string;
      entity_id?: string;
      is_active?: boolean;
    }) => {
      const { data } = await api.post<AnalyticsSchedule>(
        "/api/v1/sinsight/schedules", input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{
        name: string;
        timezone: string;
        windows: ScheduleWindow[];
        is_active: boolean;
      }>;
    }) => {
      const { data } = await api.put<AnalyticsSchedule>(
        `/api/v1/sinsight/schedules/${input.id}`, input.patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/sinsight/schedules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

// ---------------------------------------------------------------------------
// S16 — analytics module config (per-camera / per-entity enable + tuning)
// ---------------------------------------------------------------------------
export interface ModuleConfig {
  id: string;
  module_key: string;
  subject_id?: string;
  camera_id?: string | null;
  entity_id?: string | null;
  enabled: boolean;
  config: Record<string, unknown>;
  created_at?: unknown;
  updated_at?: unknown;
}

export function useModuleConfigs(filters?: {
  module_key?: string;
  camera_id?: string;
  entity_id?: string;
}) {
  return useQuery({
    queryKey: [...MODULE_CONFIG_KEY, filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.module_key) params.set("module_key", filters.module_key);
      if (filters?.camera_id) params.set("camera_id", filters.camera_id);
      if (filters?.entity_id) params.set("entity_id", filters.entity_id);
      const { data } = await api.get<{
        items: ModuleConfig[]; total: number;
      }>(`/api/v1/sinsight/modules/config?${params.toString()}`);
      return data;
    },
  });
}

export function useCreateModuleConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      module_key: string;
      camera_id?: string;
      entity_id?: string;
      enabled?: boolean;
      config?: Record<string, unknown>;
    }) => {
      const { data } = await api.post<ModuleConfig>(
        "/api/v1/sinsight/modules/config", input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MODULE_CONFIG_KEY }),
  });
}

export function useUpdateModuleConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{ enabled: boolean; config: Record<string, unknown> }>;
    }) => {
      const { data } = await api.put<ModuleConfig>(
        `/api/v1/sinsight/modules/config/${input.id}`, input.patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MODULE_CONFIG_KEY }),
  });
}

export function useDeleteModuleConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/sinsight/modules/config/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MODULE_CONFIG_KEY }),
  });
}

// ---------------------------------------------------------------------------
// S16-batch — analytics zones (polygon / line shapes), subject-keyed.
// ---------------------------------------------------------------------------
export interface AnalyticsZone {
  id: string;
  subject_id?: string;
  camera_id?: string | null;
  entity_id?: string | null;
  name: string;
  shape_type: "polygon" | "line";
  geometry: Record<string, unknown>;
  is_active: boolean;
  created_at?: unknown;
  updated_at?: unknown;
}

const ZONES_KEY = ["sinsight", "zones"] as const;

export function useZones(filters?: { camera_id?: string; shape_type?: string }) {
  return useQuery({
    queryKey: [...ZONES_KEY, filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.camera_id) params.set("camera_id", filters.camera_id);
      if (filters?.shape_type) params.set("shape_type", filters.shape_type);
      const { data } = await api.get<{ items: AnalyticsZone[]; total: number }>(
        `/api/v1/sinsight/zones?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useCreateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      shape_type: "polygon" | "line";
      geometry: Record<string, unknown>;
      camera_id?: string;
      entity_id?: string;
      is_active?: boolean;
    }) => {
      const { data } = await api.post<AnalyticsZone>(
        "/api/v1/sinsight/zones", input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ZONES_KEY }),
  });
}

export function useUpdateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{ name: string; geometry: Record<string, unknown>; is_active: boolean }>;
    }) => {
      const { data } = await api.put<AnalyticsZone>(
        `/api/v1/sinsight/zones/${input.id}`, input.patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ZONES_KEY }),
  });
}

export function useDeleteZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/sinsight/zones/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ZONES_KEY }),
  });
}

// ---------------------------------------------------------------------------
// S16-fire — assistive fire/smoke config (beta, gated off by default).
// ---------------------------------------------------------------------------
export interface FireConfig {
  id: string;
  subject_id?: string;
  camera_id?: string | null;
  entity_id?: string | null;
  enabled: boolean;
  model_profile: string;
  min_smoke_confidence: number;
  min_flame_confidence: number;
  min_bbox_area_ratio: number;
  min_consecutive_frames: number;
  persistence_seconds: number;
  max_candidate_age_seconds: number;
  cooldown_seconds: number;
  frozen_frame_n: number;
  frozen_frame_epsilon: number;
  panel_state_staleness_seconds: number;
  validation_status: string;
  assistive_not_certified: boolean;
}

const FIRE_KEY = ["sinsight", "fire-config"] as const;

export function useFireConfig(filters?: { camera_id?: string }) {
  return useQuery({
    queryKey: [...FIRE_KEY, filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.camera_id) params.set("camera_id", filters.camera_id);
      const { data } = await api.get<FireConfig[]>(
        `/api/v1/sinsight/fire/config?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useUpsertFireConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<FireConfig> & { camera_id?: string; entity_id?: string }) => {
      const { data } = await api.put<FireConfig>("/api/v1/sinsight/fire/config", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: FIRE_KEY }),
  });
}

export function useDeleteFireConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/sinsight/fire/config/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: FIRE_KEY }),
  });
}
